import { useState } from 'react';
import { TextKey } from '@mews-commander/enums';
import { NewTabLink, Select } from '@mews-commander/ui';
import { LocalizedText, useTimezone } from '@mews/framework';
import { Button, IconName, Stack } from '@optimus-web/core';
import { WithAvailabilityBlockId, WithServiceId } from '@mews-commander/types';
import { copyToClipboard, useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { getServiceBookingEnginesOptions } from '@mews-commander/legacy-entities/src/ducks/bookingEngine';
import { useServiceInfo } from '@mews-commander/legacy-entities/src/utils/useServiceInfo';
import { getPlatformPrimaryUrl } from '../../../app/reducers/configuration';
import preferences from '../../../preferences';
import { getAvailabilityBlockDateConstraints } from '../../utils/availabilityBlockDateConversions';

const {
    actions: { setPreferredBookingEngineId },
    selectors: { getPreferredBookingEngineId },
} = preferences;

interface BookingEngineLinkProps extends WithAvailabilityBlockId, WithServiceId {
    startUtc: string;
    endUtc: string;
}

export const BookingEngineLink: React.FC<BookingEngineLinkProps> = ({
    availabilityBlockId,
    serviceId,
    startUtc,
    endUtc,
}) => {
    const dispatch = useCommanderThunkDispatch();
    const timezone = useTimezone();
    const { timeUnit, isOverlappingTimeUnit } = useServiceInfo(serviceId);

    const options = useCommanderSelector((state) =>
        serviceId ? getServiceBookingEnginesOptions(state, { serviceId }) : []
    );

    const platformPrimaryUrl = useCommanderSelector(getPlatformPrimaryUrl);

    const preferredBookingEngineId = useCommanderSelector(getPreferredBookingEngineId);

    const [selectedId, setSelectedId] = useState<string | null>(preferredBookingEngineId ?? null);

    const { startDate, endDate } = getAvailabilityBlockDateConstraints(
        startUtc,
        endUtc,
        timezone,
        isOverlappingTimeUnit,
        timeUnit
    );
    const dateUrlParam = startDate && endDate ? `&mewsStart=${startDate.value}&mewsEnd=${endDate.value}` : '';
    const url = `${platformPrimaryUrl}/distributor/${selectedId}?mewsAvailabilityBlockId=${availabilityBlockId}${dateUrlParam}`;

    const handleCopy = () => {
        copyToClipboard(url);
    };

    const select = (id: string) => {
        setSelectedId(id);
        dispatch(setPreferredBookingEngineId(id));
    };

    return (
        <Stack vertical spacing="300">
            <Select
                required
                options={options}
                placeholder={<LocalizedText textKey={TextKey.SelectBookingEngine} />}
                label={<LocalizedText textKey={TextKey.BookingEngineConfiguration} />}
                selected={selectedId}
                onChange={select}
            />
            {selectedId && (
                <Stack vertical itemAlignment="center" alignment="center" spacing="300">
                    <NewTabLink url={url} icon={IconName.ExternalLink} size="small">
                        <LocalizedText textKey={TextKey.LinkToBookingEngine} />
                    </NewTabLink>

                    <Button
                        size="medium"
                        variant="ghost"
                        iconPosition="right"
                        icon={IconName.Copy}
                        onClick={handleCopy}
                        type="submit"
                    >
                        <LocalizedText textKey={TextKey.CopyToClipboard} />
                    </Button>
                </Stack>
            )}
        </Stack>
    );
};
